
import React from 'react';
import { Button } from "@/components/ui/button";
import { dataCollector } from '@/services/fraudDetection/ml/dataCollector';
import { useToast } from '@/hooks/use-toast';
import { CheckIcon, XIcon, AlertTriangleIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import { DetectionResult } from '@/services/fraudDetection/ml/mlServiceTypes';

interface FeedbackButtonProps {
  originalText: string;
  detectionResult: DetectionResult;
  onFeedbackSubmitted?: () => void;
}

export function FeedbackButton({ originalText, detectionResult, onFeedbackSubmitted }: FeedbackButtonProps) {
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [selectedFeedback, setSelectedFeedback] = React.useState<'accurate' | 'false_positive' | 'false_negative' | null>(null);
  const { toast } = useToast();

  const handleFeedback = async (feedbackType: 'accurate' | 'false_positive' | 'false_negative') => {
    if (!originalText || !detectionResult) return;
    
    setSelectedFeedback(feedbackType);
    setSubmitting(true);
    try {
      const success = await dataCollector.saveTrainingSample(
        originalText,
        detectionResult,
        feedbackType
      );
      
      if (success) {
        setSubmitted(true);
        toast({
          title: "Thank you for your feedback!",
          description: "Your input helps improve our detection models.",
          variant: "default"
        });
        
        if (onFeedbackSubmitted) {
          onFeedbackSubmitted();
        }
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      toast({
        title: "Error submitting feedback",
        description: "Please try again later.",
        variant: "destructive"
      });
      setSelectedFeedback(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center p-4 bg-green-50 rounded-md border border-green-200">
        <CheckIcon className="text-green-500 mb-2" />
        <p className="text-sm text-green-700 text-center">
          Thank you for your feedback! It helps improve our detection system.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
      <h4 className="font-medium mb-2">Was this analysis accurate?</h4>
      <p className="text-sm text-gray-600 mb-3">
        Your feedback helps improve our AI detection models
      </p>
      
      <div className="flex flex-wrap gap-2">
        <Button 
          variant={selectedFeedback === 'accurate' ? "default" : "outline"} 
          size="sm"
          onClick={() => handleFeedback('accurate')}
          disabled={submitting}
          className="flex items-center gap-1"
        >
          <ThumbsUpIcon className="h-4 w-4" />
          <span>Accurate</span>
        </Button>
        
        <Button 
          variant={selectedFeedback === 'false_positive' ? "default" : "outline"} 
          size="sm"
          onClick={() => handleFeedback('false_positive')}
          disabled={submitting}
          className="flex items-center gap-1"
        >
          <AlertTriangleIcon className="h-4 w-4" />
          <span>Should be Safe</span>
        </Button>
        
        <Button 
          variant={selectedFeedback === 'false_negative' ? "default" : "outline"} 
          size="sm"
          onClick={() => handleFeedback('false_negative')}
          disabled={submitting}
          className="flex items-center gap-1"
        >
          <ThumbsDownIcon className="h-4 w-4" />
          <span>Should be Fraudulent</span>
        </Button>
      </div>
    </div>
  );
}
